/* The lesson page is a sequence of full-bleed ACTS.
 *
 * Bands run the whole window width and constrain their own content, so a value
 * change marks a real transition rather than stopping at a column edge. The
 * unit rail is gone — see LessonNav; a 280px column made "full-bleed" a
 * contradiction and spent a quarter of the screen on navigation used twice a
 * session.
 */
.reader {
  background: var(--paper);
  /* The band runs full width; its content is CENTRED on the reading column.
     Until 2026-09-28 every act reserved a margin-note column beside the text,
     so the text sat left of centre on every page — and only the first act of a
     few lessons has notes at all. Now the column is centred, and notes take a
     gutter only where the screen is wide enough to leave the column centred
     (see the 1400px rule below); otherwise they stack under the text. */
  --act: 1080px;
  --col: calc(var(--measure) + 56px);   /* the reading column plus its padding */
}

/* an act: full width, content held to the measure */
.act { width: 100%; }
.actInner { max-width: var(--act); margin: 0 auto; padding: 40px 28px 44px; }

/* tones. Long-form reading stays on paper; inverted bands carry short,
   high-signal content only (01-explanations rule 9 — don't highlight
   everything, or nothing is highlighted). */
.read   { background: var(--paper); }
.open   { background: var(--try-bg); border-bottom: 1px solid var(--rule); }
.invert { background: var(--invert-bg); color: var(--invert-fg); }
.panel  { background: var(--panel); }

.invert h2, .invert h3, .invert strong { color: var(--invert-fg); }
.invert p, .invert li { color: color-mix(in srgb, var(--invert-fg) 82%, transparent); }
.invert table td { color: color-mix(in srgb, var(--invert-fg) 82%, transparent); border-bottom-color: color-mix(in srgb, var(--invert-fg) 16%, transparent); }
.invert table th { color: var(--invert-label); border-bottom-color: color-mix(in srgb, var(--invert-fg) 34%, transparent); }
.invert code { background: color-mix(in srgb, var(--invert-fg) 12%, transparent); color: inherit; }

/* head — its own act, above the first band */
.head { max-width: var(--col); margin: 0 auto; padding: 40px 28px 8px; }
.title { margin: 14px 0 0; font: 300 44px/1.08 var(--serif); color: var(--ink); text-wrap: balance; }
.lede { margin: 20px 0 0; max-width: var(--measure); font: 400 16.5px/1.75 var(--sans); color: var(--body); text-wrap: pretty; }
.lede strong { font-weight: 700; color: var(--ink); }

/* content column, centred; margin notes under it, or in a gutter when wide */
.grid { display: grid; gap: 32px 44px; align-items: start; justify-content: center;
        grid-template-columns: minmax(0, var(--measure)); }
.grid > * { min-width: 0; }
.margins { display: flex; flex-direction: row; flex-wrap: wrap; gap: 22px; }
.margins > * { flex: 1 1 240px; }
/* Things in an act that are not reading — practice, the prev/next foot — keep
   to the column (the foot) or a wider panel (practice), both centred. */
.practiceCol { max-width: 1024px; margin: 0 auto; }
.foot, .extraLink { max-width: var(--measure); margin-left: auto; margin-right: auto; }

.empty { margin: 0; padding: 28px 0; font: 400 15px/1.7 var(--sans); color: var(--ink-55); }
.empty code { font: 500 13px var(--mono); }

.extraLink { margin: 0; font: 400 13px var(--sans); color: var(--ink-55); }
.extraLink button {
  border: none; background: none; padding: 0; cursor: pointer;
  font: 500 13px var(--sans); color: var(--olive); text-decoration: underline;
}
.extraLink button:hover { color: var(--ink); }

.foot {
  display: flex; flex-wrap: wrap; gap: 12px; padding-top: 22px;
  border-top: 1px solid var(--rule);
  justify-content: space-between; align-items: center;
}
.prev {
  border: none; background: transparent; cursor: pointer; padding: 0;
  font: 400 12.5px var(--sans); color: var(--ink-55); text-align: left;
}
.prev:hover { color: var(--ink); }
.footActions { display: flex; gap: 10px; align-items: center; margin-left: auto; }

/* Wide enough for a gutter on BOTH sides of the column: the notes take the
   right one and the text stays centred. 240 + 44 + measure + 44 + 240. */
@media (min-width: 1400px) {
  .reader { --act: 1320px; }
  .grid.withMargins { grid-template-columns: 240px minmax(0, var(--measure)) 240px; }
  .grid.withMargins > :first-child { grid-column: 2; }
  .grid.withMargins > .margins { grid-column: 3; flex-direction: column; gap: 18px; padding-top: 4px; }
}
@media (max-width: 760px) {
  .actInner, .head { padding-left: 18px; padding-right: 18px; }
  .title { font-size: 34px; }
}

/* An assignment's repository panel (D-039): links out to the template, never a
   due date or a submit button (D-002). */
.repoLead { margin: 0 0 14px; font: 400 15px/1.6 var(--sans); color: var(--ink); max-width: var(--measure); }
.repoLinks { display: flex; flex-wrap: wrap; gap: 18px; margin: 0; font: 500 14px var(--sans); }
.repoLinks a { color: var(--olive); text-decoration: underline; }
.repoLinks a:hover { color: var(--ink); }
.repoLinks em { color: var(--ink-55); font-style: normal; }
